import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  bitsToMegabits,
  applyDeviceState,
  bytesToMegabits,
  checkDevice as checkDeviceRequest,
  createDevice as createDeviceRequest,
  deleteDevice as deleteDeviceRequest,
  dotNetTimeSpanToHours,
  getDeviceHistory,
  getDeviceTelemetry,
  getDevices,
  getDeviceStates,
  mapBackendDevice,
  mapTelemetryRecords,
  statusFromBackend,
  type CreateDeviceRequest,
} from '../api/devices';
import { MonitoringHubClient } from '../signalr/monitoringHub';
import { getEvents, setEventAcknowledgement } from '../api/events';
import type {
  CameraDetails,
  ConnectionState,
  Device,
  DeviceHistoryEntry,
  DeviceTelemetry,
  MonitoringSnapshot,
  Recorder,
  TelemetryPoint,
} from '../types';
import { useAuth } from './AuthContext';

type LoadState = 'idle' | 'loading' | 'success' | 'empty' | 'error';

interface MonitoringContextValue {
  snapshot: MonitoringSnapshot | null;
  devices: Device[];
  loadState: LoadState;
  error: string | null;
  connectionState: ConnectionState;
  isLiveData: boolean;
  lastUpdated: Date | null;
  deviceHistory: Record<string, DeviceHistoryEntry[]>;
  deviceTelemetry: Record<string, TelemetryPoint[]>;
  refresh: () => Promise<void>;
  loadDeviceHistory: (deviceId: string, limit?: number) => Promise<DeviceHistoryEntry[]>;
  loadDeviceTelemetry: (deviceId: string, limit?: number, hours?: number) => Promise<TelemetryPoint[]>;
  checkDevice: (deviceId: string) => Promise<Device | undefined>;
  createDevice: (request: CreateDeviceRequest) => Promise<Device>;
  deleteDevice: (deviceId: string) => Promise<void>;
  acknowledgeEvent: (eventId: string) => Promise<void>;
}

const MonitoringContext = createContext<MonitoringContextValue | null>(null);

const defaultRecorder: Recorder = {
  id: 'unavailable',
  name: 'Видеорегистратор',
  status: 'offline',
  ip: '—',
  channels: 0,
  usedChannels: 0,
  location: 'Нет данных',
  lastResponse: 'Нет данных',
};

const recorderFromDevices = (devices: Device[]): Recorder => {
  const recorder = devices.find((device) => device.type === 'recorder');
  if (!recorder) return defaultRecorder;
  return {
    id: recorder.id,
    name: recorder.name,
    status: recorder.status,
    ip: recorder.ip,
    channels: recorder.channelCount ?? 0,
    usedChannels: devices.filter((device) => device.camera?.recorderId === recorder.id).length,
    location: recorder.location,
    lastResponse: recorder.lastResponse,
  };
};

const mergeDefined = <T extends object>(current: T, incoming: Partial<T>): T => {
  const defined = Object.fromEntries(
    Object.entries(incoming).filter(([, value]) => value !== undefined),
  ) as Partial<T>;
  return { ...current, ...defined };
};

const reconcileDevice = (current: Device, incoming: Device): Device => {
  const merged = mergeDefined(current, incoming);
  const telemetry = mergeDefined<DeviceTelemetry>(current.telemetry, incoming.telemetry);
  const camera = current.camera || incoming.camera
    ? mergeDefined<CameraDetails>(current.camera ?? {} as CameraDetails, incoming.camera ?? {})
    : undefined;
  const result = { ...merged, telemetry, camera };
  return JSON.stringify(current) === JSON.stringify(result) ? current : result;
};

const updateSnapshotDevices = (
  current: MonitoringSnapshot | null,
  devices: Device[],
): MonitoringSnapshot | null => current && (devices.length === current.devices.length &&
  devices.every((device, index) => device === current.devices[index]) ? current : {
    ...current,
    devices,
    recorder: recorderFromDevices(devices),
  });

const appendLiveTelemetry = (
  current: Record<string, TelemetryPoint[]>,
  deviceId: string,
  sample: Omit<TelemetryPoint, 'timestamp' | 'label'>,
) => {
  const existing = current[deviceId];
  if (!existing) return current;
  const now = new Date();
  return {
    ...current,
    [deviceId]: [
      ...existing,
      {
        ...sample,
        timestamp: now.toISOString(),
        label: now.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' }),
      },
    ].slice(-3_000),
  };
};

export function MonitoringProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, profile } = useAuth();
  const [snapshot, setSnapshot] = useState<MonitoringSnapshot | null>(null);
  const [loadState, setLoadState] = useState<LoadState>('idle');
  const [error, setError] = useState<string | null>(null);
  const [connectionState, setConnectionState] = useState<ConnectionState>('disconnected');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [deviceHistory, setDeviceHistory] = useState<Record<string, DeviceHistoryEntry[]>>({});
  const [deviceTelemetry, setDeviceTelemetry] = useState<Record<string, TelemetryPoint[]>>({});
  const snapshotRef = useRef(snapshot);
  const hubRef = useRef<MonitoringHubClient | null>(null);
  const lastFullRefresh = useRef(0);
  const telemetryRequests = useRef(new Map<string, Promise<TelemetryPoint[]>>());
  const telemetryVersions = useRef(new Map<string, number>());
  const ownerGeneration = useRef(0);
  const snapshotRequest = useRef(0);
  const isLiveData = isAuthenticated;

  useEffect(() => { snapshotRef.current = snapshot; }, [snapshot]);

  useEffect(() => {
    ownerGeneration.current++;
    setSnapshot(null);
    telemetryRequests.current.clear();
    telemetryVersions.current.clear();
  }, [isAuthenticated, profile?.id]);

  const loadSnapshot = useCallback(async () => {
    if (!isAuthenticated) return;
    const generation = ownerGeneration.current;
    const request = ++snapshotRequest.current;
    const isCurrent = () => generation === ownerGeneration.current && request === snapshotRequest.current;

    setLoadState('loading');
    setError(null);
    setDeviceHistory({});
    setDeviceTelemetry({});

    // Events must not delay connecting cameras or fail the device list.
    const events = getEvents({ pageSize: 50 }).catch(() => null);
    try {
      const devices = await getDevices();
      if (!isCurrent()) return;
      lastFullRefresh.current = Date.now();
      setSnapshot((current) => ({
        devices,
        recorder: recorderFromDevices(devices),
        events: current?.events ?? [],
        telemetry: [],
      }));
      setLoadState(devices.length ? 'success' : 'empty');
      setLastUpdated(new Date());
      const eventPage = await events;
      if (eventPage && isCurrent())
        setSnapshot((current) => current && { ...current, events: eventPage.items });
    } catch (requestError) {
      if (!isCurrent()) return;
      setLoadState('error');
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'Не удалось подключиться к серверу.',
      );
      setConnectionState('disconnected');
    }
  }, [isAuthenticated, profile?.id]);

  useEffect(() => {
    if (!isAuthenticated) {
      setSnapshot(null);
      setDeviceHistory({});
      setDeviceTelemetry({});
      setLoadState('idle');
      setConnectionState('disconnected');
      return;
    }
    void loadSnapshot();
  }, [isAuthenticated, loadSnapshot]);

  useEffect(() => {
    if (!isLiveData) return;
    let active = true;
    let refreshing = false;
    const refreshCameraStates = async (full = false) => {
      if (!active || refreshing || document.visibilityState !== 'visible') return;
      refreshing = true;
      try {
        // Hub events carry metadata changes. Poll only compact live state while
        // connected, with an occasional full reconciliation for missed events.
        full ||= connectionState !== 'connected' || Date.now() - lastFullRefresh.current >= 300_000;
        const states = full ? null : await getDeviceStates();
        if (!active) return;
        if (states) {
          const knownIds = new Set(snapshotRef.current?.devices.map((device) => device.id));
          full = states.some((state) => !knownIds.has(state.id));
        }
        const devices = full ? await getDevices() : null;
        if (!active) return;
        if (devices) lastFullRefresh.current = Date.now();
        setSnapshot((current) => {
          if (!current) return current;
          const existingById = new Map(current.devices.map((device) => [device.id, device]));
          const statesById = new Map(states?.map((state) => [state.id, state]));
          const reconciled = devices
            ? devices.map((incoming) => {
                const existing = existingById.get(incoming.id);
                return existing ? reconcileDevice(existing, incoming) : incoming;
              })
            : current.devices.flatMap((device) => {
                const state = statesById.get(device.id);
                if (!state) return [];
                const incoming = applyDeviceState(device, state);
                return [JSON.stringify(device) === JSON.stringify(incoming) ? device : incoming];
              });
          return updateSnapshotDevices(current, reconciled);
        });
        const count = devices?.length ?? states?.length ?? 0;
        setLoadState((current) => current === 'loading' || current === 'idle'
          ? current : count ? 'success' : 'empty');
        setLastUpdated(new Date());
      } catch {
        if (!active) return;
        // The normal load and SignalR paths surface connectivity errors. This
        // lightweight poll only refreshes backend-computed camera state. Still
        // advance the presentation clock so a previously fresh heartbeat can
        // become "Данные устарели" while the API itself is unreachable.
        setLastUpdated(new Date());
      } finally {
        refreshing = false;
      }
    };
    const refreshVisibleDevices = () => { void refreshCameraStates(true); };
    // Mobile browsers suspend timers and can miss hub events while in the background.
    document.addEventListener('visibilitychange', refreshVisibleDevices);
    window.addEventListener('pageshow', refreshVisibleDevices);
    window.addEventListener('focus', refreshVisibleDevices);
    window.addEventListener('online', refreshVisibleDevices);
    if (connectionState === 'connected') refreshVisibleDevices();
    const interval = window.setInterval(() => { void refreshCameraStates(); }, 15_000);
    return () => {
      active = false;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', refreshVisibleDevices);
      window.removeEventListener('pageshow', refreshVisibleDevices);
      window.removeEventListener('focus', refreshVisibleDevices);
      window.removeEventListener('online', refreshVisibleDevices);
    };
  }, [isLiveData, profile?.id, connectionState]);

  const loadDeviceHistory = useCallback(async (deviceId: string, limit?: number) => {
    if (!isLiveData) return [];
    const history = await getDeviceHistory(deviceId, { limit });
    setDeviceHistory((current) => ({ ...current, [deviceId]: history }));
    return history;
  }, [isLiveData]);

  const loadDeviceTelemetry = useCallback(async (deviceId: string, limit?: number, hours?: number) => {
    if (!isLiveData) return [];
    const key = `${deviceId}:${limit}:${hours}`;
    const pending = telemetryRequests.current.get(key);
    if (pending) return pending;
    const generation = ownerGeneration.current;
    const version = (telemetryVersions.current.get(deviceId) ?? 0) + 1;
    telemetryVersions.current.set(deviceId, version);
    const request = getDeviceTelemetry(deviceId, {
      limit,
      ...(hours == null ? {} : { from: new Date(Date.now() - hours * 3_600_000).toISOString() }),
    }).then((records) => {
      const telemetry = mapTelemetryRecords(records);
      if (generation === ownerGeneration.current && version === telemetryVersions.current.get(deviceId)) {
        setDeviceTelemetry((current) => ({ ...current, [deviceId]: telemetry }));
      }
      return telemetry;
    }).finally(() => {
      if (telemetryRequests.current.get(key) === request) telemetryRequests.current.delete(key);
    });
    telemetryRequests.current.set(key, request);
    return request;
  }, [isLiveData]);

  const checkDevice = useCallback(async (deviceId: string) => {
    if (!isLiveData) return undefined;
    const checked = await checkDeviceRequest(deviceId);
    setSnapshot((current) => {
      if (!current) return current;
      const devices = current.devices.map((device) =>
        device.id === checked.id ? reconcileDevice(device, checked) : device,
      );
      return updateSnapshotDevices(current, devices);
    });
    setLastUpdated(new Date());
    return checked;
  }, [isLiveData]);

  const createDevice = useCallback(async (request: CreateDeviceRequest) => {
    if (!isLiveData) throw new Error('Для добавления камеры необходимо войти в систему.');
    const created = await createDeviceRequest(request);
    setSnapshot((current) => {
      if (!current) return current;
      const existing = current.devices.find((device) => device.id === created.id);
      const devices = existing
        ? current.devices.map((device) => device.id === created.id ? reconcileDevice(device, created) : device)
        : [...current.devices, created].sort((left, right) => left.name.localeCompare(right.name, 'ru'));
      return updateSnapshotDevices(current, devices);
    });
    setLoadState('success');
    setLastUpdated(new Date());
    return created;
  }, [isLiveData]);

  const deleteDevice = useCallback(async (deviceId: string) => {
    if (!isLiveData) throw new Error('Для удаления устройства необходимо войти в систему.');
    await deleteDeviceRequest(deviceId);
    setSnapshot((current) => current && updateSnapshotDevices(
      current,
      current.devices.filter((device) => device.id !== deviceId),
    ));
  }, [isLiveData]);

  useEffect(() => {
    if (!isLiveData) return;

    const hub = new MonitoringHubClient({
      onConnectionState: setConnectionState,
      onDeviceCreated: (backendDevice) => {
        const created = mapBackendDevice(backendDevice);
        setSnapshot((current) => {
          if (!current) return current;
          const existing = current.devices.find((device) => device.id === created.id);
          const devices = existing
            ? current.devices.map((device) => device.id === created.id ? reconcileDevice(device, created) : device)
            : [...current.devices, created].sort((left, right) => left.name.localeCompare(right.name, 'ru'));
          return updateSnapshotDevices(current, devices);
        });
        setLastUpdated(new Date());
      },
      onDeviceUpdated: (backendDevice) => {
        const updated = mapBackendDevice(backendDevice);
        setSnapshot((current) => {
          if (!current) return current;
          const existing = current.devices.find((device) => device.id === updated.id);
          const devices = existing
            ? current.devices.map((device) => device.id === updated.id ? reconcileDevice(device, updated) : device)
            : [...current.devices, updated].sort((left, right) => left.name.localeCompare(right.name, 'ru'));
          return updateSnapshotDevices(current, devices);
        });
        setLastUpdated(new Date());
      },
      onDeviceDeleted: (deviceId) => {
        setSnapshot((current) => current && updateSnapshotDevices(
          current,
          current.devices.filter((device) => device.id !== deviceId),
        ));
        setDeviceHistory((current) => {
          const remaining = { ...current };
          delete remaining[deviceId];
          return remaining;
        });
        setDeviceTelemetry((current) => {
          const remaining = { ...current };
          delete remaining[deviceId];
          return remaining;
        });
        setLastUpdated(new Date());
      },
      onDeviceStatus: (deviceId, status) => {
        setSnapshot((current) => {
          if (!current) return current;
          const devices = current.devices.map((device) =>
            device.id === deviceId
              ? {
                  ...device,
                  backendStatus: status,
                  // Camera summary is authoritative and arrives on the DTO.
                  // A low-level status event must not rebuild it in the UI.
                  ...(device.type === 'camera' ? {} : {
                    isOnline: status === 2,
                    status: statusFromBackend(status, device.health),
                  }),
                }
              : device,
          );
          return updateSnapshotDevices(current, devices);
        });
        setLastUpdated(new Date());
      },
      onMetrics: (deviceId, metrics) => {
        setSnapshot((current) => {
          if (!current) return current;
          const devices = current.devices.map((device) =>
            device.id === deviceId
              ? {
                  ...device,
                  telemetry: {
                    ...device.telemetry,
                    cpu: metrics.cpu,
                    ram: metrics.ram,
                    uptimeHours: dotNetTimeSpanToHours(metrics.uptime),
                    networkInMbps: bytesToMegabits(metrics.downloadBytesPerSecond) ?? bitsToMegabits(metrics.bandwidthBitsPerSecond) ?? device.telemetry.networkInMbps,
                    networkOutMbps: bytesToMegabits(metrics.uploadBytesPerSecond) ?? device.telemetry.networkOutMbps,
                    recordedAtUtc: new Date().toISOString(),
                    cpuRecordedAtUtc: new Date().toISOString(),
                  },
                }
              : device,
          );
          return updateSnapshotDevices(current, devices);
        });
        setDeviceTelemetry((current) => {
          const previous = current[deviceId]?.at(-1);
          return appendLiveTelemetry(current, deviceId, {
            cpu: metrics.cpu,
            ram: metrics.ram,
            networkIn: bytesToMegabits(metrics.downloadBytesPerSecond) ?? bitsToMegabits(metrics.bandwidthBitsPerSecond) ?? previous?.networkIn,
            networkOut: bytesToMegabits(metrics.uploadBytesPerSecond) ?? previous?.networkOut,
            health: previous?.health,
            heartbeat: previous?.heartbeat,
          });
        });
        setLastUpdated(new Date());
      },
      onNetworkTelemetry: (deviceId, metrics) => {
        setSnapshot((current) => {
          if (!current) return current;
          const devices = current.devices.map((device) =>
            device.id === deviceId
              ? {
                  ...device,
                  health: device.type === 'camera' ? device.health : metrics.healthScore,
                  status: device.type === 'camera' ? device.status : statusFromBackend(device.backendStatus, metrics.healthScore),
                  telemetry: {
                    ...device.telemetry,
                    networkInMbps: bytesToMegabits(metrics.downloadBytesPerSecond) ?? bitsToMegabits(metrics.bandwidthBitsPerSecond) ?? device.telemetry.networkInMbps,
                    networkOutMbps: bytesToMegabits(metrics.uploadBytesPerSecond) ?? device.telemetry.networkOutMbps,
                    latencyMs: metrics.pingMilliseconds,
                    averageLatencyMs: metrics.averagePingMilliseconds,
                    packetLoss: metrics.packetLossPercent,
                    jitterMs: metrics.jitterMilliseconds,
                    connectionQuality: metrics.connectionQuality,
                    recordedAtUtc: metrics.recordedAtUtc,
                  },
                }
              : device,
          );
          return updateSnapshotDevices(current, devices);
        });
        setDeviceTelemetry((current) => {
          const previous = current[deviceId]?.at(-1);
          return appendLiveTelemetry(current, deviceId, {
            cpu: previous?.cpu,
            ram: previous?.ram,
            networkIn: bytesToMegabits(metrics.downloadBytesPerSecond) ?? bitsToMegabits(metrics.bandwidthBitsPerSecond) ?? previous?.networkIn,
            networkOut: bytesToMegabits(metrics.uploadBytesPerSecond) ?? previous?.networkOut,
            health: metrics.healthScore,
            heartbeat: previous?.heartbeat,
          });
        });
        setLastUpdated(new Date());
      },
    });

    hubRef.current = hub;
    void hub.connect();
    return () => {
      void hub.disconnect();
      hubRef.current = null;
    };
  }, [isLiveData]);

  const acknowledgeEvent = useCallback(async (eventId: string) => {
    const updated = await setEventAcknowledgement(eventId, true);
    setSnapshot((current) =>
      current
        ? {
            ...current,
            events: current.events.map((event) =>
              event.id === eventId ? updated : event,
            ),
          }
        : current,
    );
  }, []);

  const value = useMemo(
    () => ({
      snapshot,
      devices: snapshot?.devices ?? [],
      loadState,
      error,
      connectionState,
      isLiveData,
      lastUpdated,
      deviceHistory,
      deviceTelemetry,
      refresh: loadSnapshot,
      loadDeviceHistory,
      loadDeviceTelemetry,
      checkDevice,
      createDevice,
      deleteDevice,
      acknowledgeEvent,
    }),
    [
      snapshot,
      loadState,
      error,
      connectionState,
      isLiveData,
      lastUpdated,
      deviceHistory,
      deviceTelemetry,
      loadSnapshot,
      loadDeviceHistory,
      loadDeviceTelemetry,
      checkDevice,
      createDevice,
      deleteDevice,
      acknowledgeEvent,
    ],
  );

  return <MonitoringContext.Provider value={value}>{children}</MonitoringContext.Provider>;
}

export const useMonitoring = () => {
  const context = useContext(MonitoringContext);
  if (!context) throw new Error('useMonitoring must be used inside MonitoringProvider');
  return context;
};
